import { useState, useEffect } from "react";
import type React from "react";
import { useQuery } from "wasp/client/operations";
import { getMetaApiConnection, triggerManualPoll, pauseMetaApiConnection, resumeMetaApiConnection } from "wasp/client/operations";
import { RefreshCw, Wifi, WifiOff, AlertCircle, Clock, Loader2, PauseCircle } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { Badge } from "../client/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import useTranslation from "../client/hooks/useTranslation";
import { cn } from "../client/utils";
import { useToast } from "../client/hooks/use-toast";

interface ConnectionData {
  id: string;
  status: string;
  lastSyncAt: Date | null;
  lastSyncError: string | null;
  lastSyncTradesCount: number;
  manualPollsUsedThisWeek: number;
  weeklyPollsResetAt: Date | null;
}

interface Props {
  accountId: string;
  connection?: ConnectionData | null;
  onRefetch?: () => void;
}

// Fenêtre de service : lun-ven 06:00-18:00 UTC (= 07:00-19:00 Paris heure d'hiver).
// Hors de cette fenêtre, la sync auto est suspendue (terminal undeployed).
function isOutsideServiceWindow(): boolean {
  const now = new Date();
  const day = now.getUTCDay();
  if (day === 0 || day === 6) return true;
  const hour = now.getUTCHours();
  return hour < 6 || hour >= 18;
}

function isWeekendParis(): boolean {
  const day = new Date().getUTCDay();
  return day === 0 || day === 6;
}

export default function MetaApiSyncStatus({ accountId, connection: externalConnection, onRefetch }: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const { data: ownConnection, refetch: ownRefetch } = useQuery(
    getMetaApiConnection,
    { accountId },
    { enabled: externalConnection === undefined },
  );

  const connection = externalConnection !== undefined ? externalConnection : ownConnection;
  const refetch = onRefetch ?? ownRefetch;

  useEffect(() => {
    if (connection?.status !== "syncing") return;
    const id = setInterval(() => refetch(), 4000);
    return () => clearInterval(id);
  }, [connection?.status, refetch]);

  if (!connection) return null;

  const s = connection.status as string;

  // Le dump JSON brut de l'API MetaAPI ne doit jamais s'afficher tel quel.
  // On détecte les erreurs transitoires (timeout / 5xx) pour un message rassurant.
  const rawError = connection.lastSyncError ?? "";
  const isTransient = /timed out|timeout|HTTP 5\d{2}|HTTP 429/i.test(rawError);
  const friendlyError = isTransient
    ? t.metaApi.errorTransient
    : t.metaApi.errorFriendly;
  const inDeployWindow = !isOutsideServiceWindow();
  const pollsUsed = connection.manualPollsUsedThisWeek ?? 0;
  const quotaExhausted = !inDeployWindow && pollsUsed >= 5;

  const handlePoll = async () => {
    setLoading(true);
    try {
      await triggerManualPoll({ accountId });
      toast({ title: t.metaApi.syncBadge.jobQueued });
      refetch();
    } catch (err: any) {
      const msg = err?.message ?? "";
      if (msg.includes("429") || msg.includes("quota")) {
        toast({ title: t.metaApi.errors.quotaExhausted, variant: "destructive" });
      } else if (msg.includes("409")) {
        toast({ title: t.metaApi.errors.syncInProgress, variant: "destructive" });
      } else {
        toast({ title: t.metaApi.errors.syncFailed, description: msg, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    setLoading(true);
    try {
      await pauseMetaApiConnection({ accountId });
      refetch();
    } catch (err: any) {
      toast({ title: t.metaApi.errors.syncFailed, description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    setLoading(true);
    try {
      await resumeMetaApiConnection({ accountId });
      refetch();
    } catch (err: any) {
      toast({ title: t.metaApi.errors.syncFailed, description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const statusIcon: Record<string, React.ReactNode> = {
    idle: <Wifi className="h-3 w-3" />,
    active: <Wifi className="h-3 w-3" />,
    pending: <Clock className="h-3 w-3 animate-pulse" />,
    syncing: <Loader2 className="h-3 w-3 animate-spin" />,
    error: <AlertCircle className="h-3 w-3" />,
    paused: <PauseCircle className="h-3 w-3" />,
    deprovisioned: <WifiOff className="h-3 w-3" />,
  };

  const statusColor: Record<string, string> = {
    idle: "text-primary border-primary/30 bg-primary/10",
    active: "text-primary border-primary/30 bg-primary/10",
    pending: "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10",
    syncing: "text-secondary border-secondary/30 bg-secondary/10",
    error: "text-destructive border-destructive/30 bg-destructive/10",
    paused: "text-muted-foreground border-muted/30 bg-muted/10",
    deprovisioned: "text-muted-foreground border-muted/30 bg-muted/10",
  };

  const statusLabel = (t.metaApi.status as any)[s] ?? s;
  const weekend = isWeekendParis();
  const outsideWindow = isOutsideServiceWindow();
  const night = outsideWindow && !weekend;

  const pollsBar = Array.from({ length: 5 }, (_, i) => i < pollsUsed ? "█" : "░").join("");

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="outline"
          className={cn("flex items-center gap-1 text-xs font-medium", statusColor[s] ?? "")}
        >
          {statusIcon[s] ?? <WifiOff className="h-3 w-3" />}
          {statusLabel}
        </Badge>

        {s === "syncing" && (
          <span className="text-xs text-muted-foreground animate-pulse">
            {t.metaApi.syncBadge.initialSyncInProgress}
          </span>
        )}

        {(s === "idle" || s === "active") && connection.lastSyncAt && (
          <span className="text-xs text-muted-foreground">
            {t.metaApi.syncBadge.lastSync}:{" "}
            {formatDistanceToNow(new Date(connection.lastSyncAt), { addSuffix: true })}
          </span>
        )}

        {s === "error" && (
          <span
            className={cn(
              "text-xs truncate max-w-[240px]",
              isTransient ? "text-muted-foreground" : "text-destructive",
            )}
            title={rawError || undefined}
          >
            {friendlyError}
          </span>
        )}
      </div>

      {/* Night / weekend badge */}
      {(night || weekend) && (s === "idle" || s === "active") && (
        <span className="text-xs text-muted-foreground">
          {weekend ? t.metaApi.syncBadge.suspendedWeekend : t.metaApi.syncBadge.suspendedNight}
        </span>
      )}

      {/* Poll quota — shown only outside deploy window */}
      {!inDeployWindow && (s === "idle" || s === "active" || s === "error") && (
        <span className="text-xs text-muted-foreground font-mono">
          {t.metaApi.syncBadge.pollQuota} : {pollsBar} {pollsUsed}/5 · {t.metaApi.syncBadge.pollQuotaReset}
        </span>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        {(s === "idle" || s === "active") && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs"
            onClick={handlePoll}
            disabled={loading || quotaExhausted}
            title={quotaExhausted ? t.metaApi.syncBadge.pollQuotaExhausted : undefined}
          >
            <RefreshCw className={cn("h-3 w-3 mr-1", loading && "animate-spin")} />
            {t.metaApi.syncNow}
          </Button>
        )}

        {/* Erreur : bouton principal = Réessayer (re-provisionne le terminal). */}
        {s === "error" && (
          <Button
            size="sm"
            variant="default"
            className="h-6 px-2 text-xs"
            onClick={handleResume}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                {t.metaApi.retrying}
              </>
            ) : (
              <>
                <RefreshCw className="h-3 w-3 mr-1" />
                {t.metaApi.retry}
              </>
            )}
          </Button>
        )}

        {(s === "idle" || s === "active" || s === "error") && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs text-muted-foreground"
            onClick={handlePause}
            disabled={loading}
          >
            {t.metaApi.deactivate}
          </Button>
        )}

        {s === "paused" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs"
            onClick={handleResume}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                {t.metaApi.retrying}
              </>
            ) : (
              t.metaApi.reactivate
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
